import React, { useEffect, useMemo, useState } from "react";
import { useI18n } from "@/i18n/hooks";
// 直接具体路径，避免走 components / modules barrel 造成跨模块循环依赖
import { Button } from "../Base/Button";
import { MailIconWithBadge } from "../../modules/notification/MailIconWithBadge";
import { OptimizedImage } from "../Base/OptimizedImage";
import { useUserDataFlattened } from "../../modules/user/stores";
import {
  useEnableDownloadAppFloatingOnHomeTop,
  useLoginRegisterConfig,
  usePlatformLogo,
} from "../../modules/homeDisplay/hooks";
import { useHomeDisplayStore } from "../../modules/homeDisplay/stores";
import { useCountryFlag } from "../../hooks/useCountryFlag";
import { useCurrencyIcon, useCurrencySymbol } from "../../hooks/useUtils";
import { useNavigation, ROUTES } from "../../utils/navigation";
import { getCountryIdFromStorage } from "../../utils/countryCookie";
import { triggerPopup } from "../../utils/popupTriggers";
import { POPUP_CONFIGS } from "../../config/popupConfig";
import "./Header.scss";
import type { HeaderProps } from "./Header.types";
import type { RedirectConfigItem } from "../../modules/homeDisplay/types";
import GiftImg from "@/assets/header/Gift.webp";
import CountryRingImg from "@/assets/header/country-ring.webp";
import { utils } from "../..";
import { isDownloadAppBarSessionClosed } from "../../utils/downloadAppBarSession";

export const Header: React.FC<HeaderProps> = ({
  title,
  className = "",
  onNotificationClick,
  onLogoClick,
}) => {
  const { t } = useI18n();
  const { navigate } = useNavigation();
  const {
    isAuthenticated,
    data,
    firstRechargeFlag,
    firstRechargeRuleAmount,
    currSymbol,
    taskBonusBalance,
  } = useUserDataFlattened();
  const currencyIcon = useCurrencyIcon();
  const enableFloatingOnHomeTop = useEnableDownloadAppFloatingOnHomeTop();
  /** 顶部展示：主余额 + 任务彩金余额（与账户总览语义对齐） */
  const displayBalance = useMemo(() => {
    const b = typeof data?.balance === "number" ? data.balance : 0;
    const task = typeof taskBonusBalance === "number" ? taskBonusBalance : 0;
    return b + task;
  }, [data?.balance, taskBonusBalance]);
  const hookCurrencySymbol = useCurrencySymbol();
  const currencySymbol =
    (typeof currSymbol === "string" && currSymbol.trim()) ||
    hookCurrencySymbol ||
    "";

  const platformLogo = usePlatformLogo();
  const logoSrc = utils.getImageUrl(platformLogo?.logoUrl || "");
  const redirectConfigs = useHomeDisplayStore(
    state => state.data?.redirectConfigs ?? []
  );
  const { getFlagSrcByCode } = useCountryFlag();
  const hasCountryList =
    Array.isArray(redirectConfigs) && redirectConfigs.length > 0;

  const { currentCountryDisplayName, currentCountryFlagCode } = useMemo(() => {
    const countryId =
      (utils.getObject("countryId") as string) ||
      getCountryIdFromStorage() ||
      (window._config?.countryId ?? "");
    const item = redirectConfigs.find(
      (c: RedirectConfigItem) =>
        (c.countryRedirectId ?? "").trim().toUpperCase() ===
        countryId.trim().toUpperCase()
    );
    const name =
      item?.countryName ?? item?.displayName ?? item?.name ?? item?.id ?? "";
    const raw = (item?.countryRedirectId ?? countryId ?? "").toString().trim();
    const flagCode = raw.length >= 2 ? raw.slice(0, 2).toLowerCase() : null;
    if (name) {
      return {
        currentCountryDisplayName: name,
        currentCountryFlagCode: flagCode,
      };
    }
    if (countryId.trim()) {
      const fromConfig = window._config?.countryList?.find(
        (c: { id?: string }) =>
          (c.id ?? "").trim().toUpperCase() === countryId.trim().toUpperCase()
      );
      const displayName =
        fromConfig?.name ?? fromConfig?.displayName ?? countryId.trim();
      const code = (countryId ?? "").slice(0, 2).toLowerCase();
      return {
        currentCountryDisplayName: displayName,
        currentCountryFlagCode: code || null,
      };
    }
    return { currentCountryDisplayName: "", currentCountryFlagCode: null };
  }, [redirectConfigs]);

  const currentCountryFlagSrc = getFlagSrcByCode(currentCountryFlagCode);

  const displayBonus = useMemo(() => {
    if (!firstRechargeFlag && (firstRechargeRuleAmount ?? 0) > 0) {
      const amt =
        Math.floor((Number(firstRechargeRuleAmount) || 0) * 100) / 100;
      return `+ ${currencySymbol}${amt}`;
    }
    return "";
  }, [firstRechargeRuleAmount, currencySymbol, firstRechargeFlag]);
  const loginRegisterConfig = useLoginRegisterConfig();
  const homeRegisterBtnText =
    loginRegisterConfig?.homeRegisterBtnFirstChargeText?.trim() || "";
  const headerDisplayBonus = homeRegisterBtnText || displayBonus;

  const [downloadAppBarVisible, setDownloadAppBarVisible] = useState(() => {
    if (utils.isApp() || utils.updateCheckIsPwa() || !enableFloatingOnHomeTop)
      return false;
    return !isDownloadAppBarSessionClosed();
  });
  useEffect(() => {
    const handler = (e: Event) =>
      setDownloadAppBarVisible((e as CustomEvent).detail);
    window.addEventListener("downloadAppBarChange", handler);
    return () => window.removeEventListener("downloadAppBarChange", handler);
  }, []);
  // 检查用户是否已注册过
  const hasRegistered = utils.getObject("has_registered");

  const handleLoginClick = () => {
    navigate(ROUTES.LOGIN);
  };

  const handleRegisterClick = () => {
    navigate(ROUTES.REGISTER);
  };

  const handleBalanceClick = () => {
    navigate(ROUTES.ACCOUNT_DETAILS);
  };

  const handleGiftClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    console.log("Gift clicked");
    // TODO: 实现礼物功能
  };

  const handleDepositClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // 阻止事件冒泡
    navigate(ROUTES.RECHARGE);
  };

  const handleMessageClick = () => {
    navigate(`${ROUTES.MESSAGE_CENTER}?tabIndex=2`);
    if (onNotificationClick) {
      onNotificationClick();
    }
  };

  const handleCountryClick = () => {
    triggerPopup(POPUP_CONFIGS.COUNTRY_LIST.id);
  };

  const displayTitle = title || t("common.title");

  return (
    <header
      className={`mobile-header ${className}`}
      style={{ marginTop: downloadAppBarVisible ? "3rem" : "0" }}
    >
      <div className="mobile-header__content">
        {/* 左侧区域：Logo */}
        <div className="mobile-header__left">
          <div
            className={`mobile-header__logo${onLogoClick ? " mobile-header__logo--clickable" : ""}`}
            onClick={onLogoClick}
            role={onLogoClick ? "button" : undefined}
            tabIndex={onLogoClick ? 0 : undefined}
            onKeyDown={
              onLogoClick
                ? e => {
                    if (e.key === "Enter" || e.key === " ") {
                      onLogoClick();
                    }
                  }
                : undefined
            }
          >
            <OptimizedImage
              loading="eager"
              src={logoSrc}
              alt={displayTitle}
              priority={true}
            />
          </div>
        </div>

        {/* 右侧功能区 */}
        <div className="mobile-header__actions">
          {hasCountryList && (
            <div
              className="mobile-header__country"
              role="button"
              tabIndex={0}
              onClick={handleCountryClick}
              onKeyDown={e => {
                if (e.key === "Enter" || e.key === " ") {
                  e.preventDefault();
                  handleCountryClick();
                }
              }}
              aria-label={currentCountryDisplayName || "Select country"}
            >
              <span className="mobile-header__country-frame">
                {currentCountryFlagSrc && (
                  <img
                    src={currentCountryFlagSrc}
                    alt=""
                    className="mobile-header__country-flag"
                    width={30}
                    height={30}
                  />
                )}
                <img
                  src={CountryRingImg}
                  alt=""
                  className="mobile-header__country-ring"
                  width={32}
                  height={32}
                  aria-hidden
                />
              </span>
            </div>
          )}
          {isAuthenticated ? (
            <>
              {/* 金币显示区域 */}
              <div
                className="mobile-header__balance-wrapper"
                onClick={handleBalanceClick}
              >
                {/* 礼物按钮 */}
                {headerDisplayBonus && (
                  <div
                    className="mobile-header__gift-btn"
                    onClick={handleGiftClick}
                  >
                    <OptimizedImage
                      loading="eager"
                      src={GiftImg}
                      alt="Gift"
                      className="gift-icon"
                      priority={true}
                    />
                    {headerDisplayBonus}
                  </div>
                )}

                {/* 余额 */}
                <div className="mobile-header__balance">
                  <OptimizedImage
                    loading="eager"
                    src={utils.getImageUrl(currencyIcon ?? "")}
                    alt="Coin"
                    // width={14}
                    // height={14}
                    className="coincoin-coin"
                    priority={true}
                  />
                  <span className="balance-amount">
                    {displayBalance.toString().split(".")[1]?.length > 2
                      ? displayBalance.toFixed(2)
                      : displayBalance}
                  </span>
                  {/* <div className="mobile-header__refresh">
                    <Icon
                      name="refresh"
                      size={14}
                      variant="golden"
                      onClick={onRefreshClick}
                    />
                  </div> */}
                </div>
                {/* 充值按钮 */}
                <Button
                  variant="primary"
                  size="sm"
                  className="mobile-header__deposit-btn"
                  onClick={handleDepositClick}
                >
                  {t("common.deposit")}
                </Button>
              </div>

              {/* 邮件图标 */}
              <div className="mobile-header__mail">
                <MailIconWithBadge
                  size={24}
                  color="#fff9c7"
                  onClick={handleMessageClick}
                  showCount={true}
                  maxCount={99}
                />
              </div>
            </>
          ) : (
            <>
              {/* 未登录按钮组 */}
              <Button
                variant="outline"
                size="sm"
                className="mobile-header__login-btn"
                onClick={handleLoginClick}
              >
                {t("common.logIn")}
              </Button>
              <div className="mobile-header__register-btn-group">
                <Button
                  variant="primary"
                  size="sm"
                  className={`mobile-header__register-btn ${!hasRegistered ? "mobile-header__register-btn--pulse" : ""}`}
                  onClick={handleRegisterClick}
                >
                  {t("common.register")}
                </Button>
                {headerDisplayBonus && (
                  <div
                    className="mobile-header__gift-btn"
                    onClick={handleGiftClick}
                  >
                    <OptimizedImage
                      loading="eager"
                      src={GiftImg}
                      alt="Gift"
                      className="gift-icon"
                      priority={true}
                    />
                    {headerDisplayBonus}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
      </div>
    </header>
  );
};

export default Header;
